import { useState, useEffect } from 'react'
import { Table, Button, Space, Tag, message, Modal, Descriptions, List, Card } from 'antd'
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons'
import { submissionApi } from '../../api/auth'

interface Submission {
  id: number
  assignment_id: number
  assignment_title: string
  student_id: number
  student_name?: string
  score: number
  feedback: string
  status: string
  submitted_at: string
  graded_at: string
}

export default function Submissions() {
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [loading, setLoading] = useState(false)
  const [feedbackModal, setFeedbackModal] = useState(false)
  const [currentSubmission, setCurrentSubmission] = useState<Submission | null>(null)

  useEffect(() => {
    fetchSubmissions()
  }, [])

  const fetchSubmissions = async () => {
    setLoading(true)
    try {
      const data = await submissionApi.list()
      setSubmissions(data.submissions || [])
    } catch (error) {
      message.error('获取提交记录失败')
    } finally {
      setLoading(false)
    }
  }

  const handleViewFeedback = (record: Submission) => {
    setCurrentSubmission(record)
    setFeedbackModal(true)
  }

  const handleRegrade = async (id: number) => {
    try {
      await submissionApi.grade(id)
      message.success('重新评分成功')
      fetchSubmissions()
    } catch (error) {
      message.error('重新评分失败')
    }
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: '题目',
      dataIndex: 'assignment_title',
      key: 'assignment_title'
    },
    {
      title: '学生',
      dataIndex: 'student_name',
      key: 'student_name',
      render: (name: string) => name || '未知'
    },
    {
      title: '得分',
      dataIndex: 'score',
      key: 'score',
      render: (score: number) => {
        if (score !== undefined && score !== null) {
          return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{score}分</span>
        }
        return '-'
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: any = {
          'pending': { color: 'orange', text: '待评分' },
          'graded': { color: 'green', text: '已评分' },
          'returned': { color: 'blue', text: '已退回' }
        }
        const { color, text } = statusMap[status] || { color: 'default', text: '未知' }
        return <Tag color={color}>{text}</Tag>
      }
    },
    {
      title: '提交时间',
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      render: (date: string) => new Date(date).toLocaleString()
    },
    {
      title: '评分时间',
      dataIndex: 'graded_at',
      key: 'graded_at',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Submission) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewFeedback(record)}
          >
            查看详情
          </Button>
          <Button
            icon={<ReloadOutlined />}
            size="small"
            onClick={() => handleRegrade(record.id)}
          >
            重新评分
          </Button>
        </Space>
      )
    }
  ]

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>学生提交记录</h2>
      <Table
        columns={columns}
        dataSource={submissions}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title="提交详情"
        open={feedbackModal}
        onCancel={() => setFeedbackModal(false)}
        footer={[
          <Button key="close" onClick={() => setFeedbackModal(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {currentSubmission && (
          <div>
            <Card style={{ marginBottom: 16 }}>
              <Descriptions column={2}>
                <Descriptions.Item label="题目">{currentSubmission.assignment_title}</Descriptions.Item>
                <Descriptions.Item label="学生">{currentSubmission.student_name || '未知'}</Descriptions.Item>
                <Descriptions.Item label="得分">
                  <span style={{ fontSize: 24, color: '#52c41a', fontWeight: 'bold' }}>
                    {currentSubmission.score}分
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                  <Tag color={currentSubmission.status === 'graded' ? 'green' : 'orange'}>
                    {currentSubmission.status === 'graded' ? '已评分' : '待评分'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="提交时间">
                  {new Date(currentSubmission.submitted_at).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="评分时间">
                  {currentSubmission.graded_at ? new Date(currentSubmission.graded_at).toLocaleString() : '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {currentSubmission.feedback && (
              <Card title="详细反馈">
                <List
                  dataSource={JSON.parse(currentSubmission.feedback || '[]')}
                  renderItem={(item: any) => (
                    <List.Item>
                      <Space>
                        {item.status === 'correct' ? (
                          <span style={{ color: '#52c41a' }}>✓</span>
                        ) : (
                          <span style={{ color: '#ff4d4f' }}>✗</span>
                        )}
                        <span>
                          单元格 <strong>{item.cell}</strong>: {item.message}
                        </span>
                      </Space>
                    </List.Item>
                  )}
                />
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
